import { html, nothing, type TemplateResult, type SVGTemplateResult } from "lit-html";
import type { Target, Opportunity, QueryResult, ScorecardRow } from "./types.js";
import { icons } from "./icons.js";
import { money, moneyShort, pct, signedPct, paceStatus, formatMetric } from "./format.js";
import { showTip, hideTip, tipRow } from "./tooltip.js";

export function pacePill(pace: number | null, compact = false): TemplateResult {
  const st = paceStatus(pace);
  // Floor, never round: 99.8% must read 99%, not a "100%" that is still flagged as close.
  const shown = pace === null ? "—" : `${Math.floor(pace * 100)}%`;
  if (st === "neutral") return html`<span class="pill neutral">${compact ? "New" : "No prior year"}</span>`;
  const icon = st === "good" ? icons.check : st === "warn" ? icons.clock : icons.alert;
  const word = st === "good" ? "On pace" : st === "warn" ? "Close" : "Behind";
  return html`<span class="pill ${st}" title="Year-to-date vs. seasonal target-to-date">${icon}${compact ? shown : `${word} · ${shown}`}</span>`;
}

export function delta(cur: number, prior: number): TemplateResult {
  if (prior === 0) return html`<span class="delta">—</span>`;
  const d = (cur - prior) / Math.abs(prior);
  return html`<span class="delta ${d >= 0 ? "up" : "down"}">${signedPct(d)}</span>`;
}

/** YTD vs full-year target, with a tick where they should be by today. */
export function meter(t: Target): TemplateResult {
  if (t.target === null || t.target <= 0) return html`<div class="meter"></div>`;
  const fill = Math.min(100, (t.ytd / t.target) * 100);
  const mark = Math.min(100, ((t.targetYtd ?? 0) / t.target) * 100);
  const st = paceStatus(t.pace);
  const tip = (e: PointerEvent) => showTip(html`<div class="tt-title">Progress to target</div>
    ${tipRow("YTD", money(t.ytd), "var(--series-1)")}
    ${tipRow("Should be at by now", money(t.targetYtd))}
    ${tipRow("Full-year target", money(t.target))}
    ${tipRow("Projected", money(t.projected))}`, e.clientX, e.clientY);
  return html`<div class="meter" @pointermove=${tip} @pointerleave=${hideTip}><b class=${st === "neutral" ? "" : st} style="width:${fill.toFixed(1)}%"></b><i style="left:calc(${mark.toFixed(1)}% - 1px)"></i></div>`;
}

export function kpi(label: string, value: string, foot: TemplateResult | string | typeof nothing = nothing, title?: string): TemplateResult {
  return html`<div class="glass kpi" title=${title ?? ""}>
    <div class="kpi-label">${label}</div>
    <div class="kpi-value">${value}</div>
    <div class="kpi-foot">${foot}</div>
  </div>`;
}

export function targetKpis(t: Target, growth: number): TemplateResult {
  return html`
    ${kpi("Revenue YTD", t.ytd >= 1e6 ? moneyShort(t.ytd) : money(t.ytd), html`${delta(t.ytd, t.priorYtd)}<span>vs ${moneyShort(t.priorYtd)} same time last year</span>`)}
    ${kpi("Pace to target", t.pace === null ? "—" : `${Math.floor(t.pace * 100)}%`, html`${pacePill(t.pace)}<span>target +${Math.round(growth * 100)}% over last year</span>`, "YTD revenue divided by where the target says we should be today, using last year's seasonality")}
    ${kpi("Projected full year", moneyShort(t.projected), t.gap === null ? html`<span>No prior year to compare</span>` : html`<span class="delta ${t.gap >= 0 ? "up" : "down"}">${t.gap >= 0 ? "+" : "−"}${moneyShort(Math.abs(t.gap))}</span><span>vs ${moneyShort(t.target)} target</span>`)}
    ${kpi("Gap to target today", t.targetYtd === null ? "—" : moneyShort(t.ytd - t.targetYtd), html`<span>${t.targetYtd === null ? "" : `should be at ${moneyShort(t.targetYtd)}`}</span>`)}
  `;
}

const OPP_META: Record<Opportunity["type"], { label: string; icon: SVGTemplateResult }> = {
  lapsed: { label: "Lapsed customer", icon: icons.userX },
  declining: { label: "Declining customer", icon: icons.trendDown },
  open_quote: { label: "Open quote", icon: icons.clock },
  low_conversion: { label: "Low conversion county", icon: icons.funnel },
  cold_county: { label: "County gone cold", icon: icons.snow },
};
export const oppLabel = (t: Opportunity["type"]) => OPP_META[t].label;

export function nsLink(url: string, label = "Open in NetSuite"): TemplateResult {
  return html`<a class="link ns" href=${url} target="_blank" rel="noopener noreferrer" title="Opens NetSuite in a new tab">${label} ↗</a>`;
}

export function oppItem(o: Opportunity, onCounty?: (fips: string) => void): TemplateResult {
  const m = OPP_META[o.type];
  const valueLabel = o.type === "open_quote" ? "quoted" : o.type === "low_conversion" ? "quotes" : o.type === "cold_county" ? "behind" : "at stake";
  return html`<div class="opp">
    <div class="opp-ic ${o.type}" title=${m.label}>${m.icon}</div>
    <div style="min-width:0">
      <div class="opp-title">${o.customerId ? html`<a class="plain" href=${`#/customer/${encodeURIComponent(o.customerId)}`}>${o.title}</a>` : o.title}</div>
      <div class="opp-detail">${o.detail}</div>
      <div class="opp-meta">${o.afterApparatus ? html`<span class="tag" style="margin:0 6px 0 0">After truck</span>` : nothing}${m.label} · ${o.repName}${o.nsUrl ? html` · ${nsLink(o.nsUrl, o.type === "open_quote" ? "Open quote in NetSuite" : "Open in NetSuite")}` : nothing}${o.countyLabel && o.type !== "low_conversion" && o.type !== "cold_county"
        ? html` · ${onCounty && o.county ? html`<button class="link" @click=${() => onCounty(o.county!)}>${o.countyLabel}</button>` : o.countyLabel}` : nothing}
        ${(o.type === "low_conversion" || o.type === "cold_county") && onCounty && o.county ? html` · <button class="link" @click=${() => onCounty(o.county!)}>Show on map</button>` : nothing}</div>
    </div>
    <div class="opp-val">${o.type === "low_conversion" ? o.value : moneyShort(o.value)}<small>${valueLabel}</small></div>
  </div>`;
}

export function resultTable(r: QueryResult, opts: { onRow?: (row: QueryResult["rows"][number]) => void; maxHeight?: number } = {}): TemplateResult {
  if (!r.rows.length) return html`<div class="empty-state">No sales match this selection.</div>`;
  const hasPrior = !!r.rows[0]?.prior;
  return html`<div class="table-wrap" style=${opts.maxHeight ? `max-height:${opts.maxHeight}px` : ""}><table class="t">
    <thead><tr>
      ${r.dims.map((d) => html`<th>${d.label}</th>`)}
      ${r.metrics.map((m) => html`<th class="r">${m.label}</th>${hasPrior ? html`<th class="r">Prior yr</th><th class="r">Change</th>` : nothing}`)}
    </tr></thead>
    <tbody>${r.rows.map((row) => html`<tr class=${opts.onRow ? "click" : ""} @click=${opts.onRow ? () => opts.onRow!(row) : nothing}>
      ${r.dims.map((d, i) => html`<td class=${i === 0 ? "name" : ""} title=${row.label[d.key] ?? ""}>${row.label[d.key]}</td>`)}
      ${r.metrics.map((m) => {
        const v = row.values[m.key] ?? null, p = row.prior?.[m.key] ?? null;
        return html`<td class="r">${formatMetric(v, m.format)}</td>${hasPrior ? html`<td class="r muted">${formatMetric(p, m.format)}</td><td class="r">${v !== null && p ? delta(v, p) : "—"}</td>` : nothing}`;
      })}
    </tr>`)}</tbody>
    ${r.dims.length ? html`<tfoot><tr>
      <td colspan=${r.dims.length}>Total${r.truncated ? " (all rows)" : ""}</td>
      ${r.metrics.map((m) => html`<td class="r">${formatMetric(r.totals[m.key], m.format)}</td>${hasPrior ? html`<td class="r muted">${formatMetric(r.priorTotals?.[m.key], m.format)}</td><td class="r">${r.totals[m.key] !== null && r.priorTotals?.[m.key] ? delta(r.totals[m.key]!, r.priorTotals[m.key]!) : "—"}</td>` : nothing}`)}
    </tr></tfoot>` : nothing}
  </table></div>`;
}

export function downloadCsv(name: string, r: QueryResult) {
  const esc = (v: unknown) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const hasPrior = !!r.rows[0]?.prior;
  const head = [...r.dims.map((d) => d.label), ...r.metrics.flatMap((m) => (hasPrior ? [m.label, `${m.label} (prior year)`] : [m.label]))];
  const lines = r.rows.map((row) => [
    ...r.dims.map((d) => row.label[d.key]),
    ...r.metrics.flatMap((m) => (hasPrior ? [row.values[m.key], row.prior?.[m.key]] : [row.values[m.key]])),
  ]);
  const csv = [head, ...lines].map((l) => l.map(esc).join(",")).join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `${name.replace(/[^\w-]+/g, "_")}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function sectionHead(title: string, sub?: string, right?: TemplateResult): TemplateResult {
  return html`<div class="card-head"><div><div class="card-title">${title}</div>${sub ? html`<div class="card-sub">${sub}</div>` : nothing}</div><span class="spacer"></span>${right ?? nothing}</div>`;
}



/** Security & Extinguisher people and accounts aren't sales reps; their numbers live on the Divisions page. */
export const repPageRows = (rows: ScorecardRow[]) => rows.filter((r) => r.repId !== null && r.primaryDivision !== "secext");
